import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { pick, union, without } from "lodash-es"
import * as API from "services/api"
import { errorToast, successToast } from "shared/toast"
import { REPORT_SFTP_FEATURE_NAME } from "utils/addOnFeatureHelpers"
import {
  MY_REPORTS_OPTION,
  NOT_SCHEDULED_SFTP_OPTION,
  SCHEDULED_ACTIVE_SFTP_OPTION,
  SCHEDULED_INACTIVE_SFTP_OPTION,
  SHARED_WITH_ME_OPTION,
  SOLUTION_REPORT_TYPE,
  SUBMISSION_REPORT_TYPE,
  SURVEY_REPORT_TYPE,
  TASK_REPORT_TYPE,
} from "utils/reportHelpers"
import { subscribedToAddOnFeature } from "reduxSlices/addOnFeatureSubscriptionsSlice"
import { currentUserId, currentUserIsInAdminRole } from "reduxSlices/sessionSlice"

const SLICE_NAME = "reportIndex"

const NO_REPORTS = {
  [SUBMISSION_REPORT_TYPE]: [],
  [SOLUTION_REPORT_TYPE]: [],
  [SURVEY_REPORT_TYPE]: [],
  [TASK_REPORT_TYPE]: [],
}

// Filter selections are stored as the options the user has turned OFF, so that an option the user
// has never seen -- a solution added after the selections were persisted -- is shown by default.
const INITIAL_STATE = {
  reports: NO_REPORTS,
  deselectedCategorySlugs: [],
  deselectedOwnershipOptions: [],
  deselectedSftpOptions: [],
}

const ownershipOption = (report, userId) => (
  report.creatorId === userId ? MY_REPORTS_OPTION : SHARED_WITH_ME_OPTION
)

// Only an active schedule is uploaded; the worker skips the inactive ones.
const sftpOption = ({ scheduledReportExport }) => {
  if (!scheduledReportExport) return NOT_SCHEDULED_SFTP_OPTION

  return scheduledReportExport.scheduledReportExportSchedule?.active
    ? SCHEDULED_ACTIVE_SFTP_OPTION
    : SCHEDULED_INACTIVE_SFTP_OPTION
}

// The SFTP filter is only applied when its sidebar section is shown.  Otherwise a selection made
// while the section was available could go on hiding reports with no way to undo it.
const sftpFilterApplies = (state) => (
  currentUserIsInAdminRole(state) && subscribedToAddOnFeature(REPORT_SFTP_FEATURE_NAME)(state)
)

const reportIsVisible = (report, filters) => {
  const {
    applySftpFilter,
    deselectedCategorySlugs,
    deselectedOwnershipOptions,
    deselectedSftpOptions,
    userId,
  } = filters

  if (deselectedCategorySlugs.includes(report.categorySlug)) return false
  if (deselectedOwnershipOptions.includes(ownershipOption(report, userId))) return false
  if (applySftpFilter && deselectedSftpOptions.includes(sftpOption(report))) return false

  return true
}

const filtersFromState = (state) => ({
  ...pick(state[SLICE_NAME], [
    "deselectedCategorySlugs",
    "deselectedOwnershipOptions",
    "deselectedSftpOptions",
  ]),
  applySftpFilter: sftpFilterApplies(state),
  userId: currentUserId(state),
})

const deleteReportRequest = (reportType, reportId) => {
  switch (reportType) {
  case SUBMISSION_REPORT_TYPE:
    return API.deleteFormSubmissionReport({ id: reportId })
  case SOLUTION_REPORT_TYPE:
    return API.deleteSolutionLevelReport({ id: reportId })
  case SURVEY_REPORT_TYPE:
    return API.deleteSurveyReport({ id: reportId })
  case TASK_REPORT_TYPE:
    return API.deleteTaskReport({ id: reportId })
  default:
    return null
  }
}

const deleteScheduledReportExportRequest = (reportType, reportId, scheduledReportExportId) => {
  switch (reportType) {
  case SUBMISSION_REPORT_TYPE:
    return API.destroyFormSubmissionScheduledReportExport({ reportId, scheduledReportExportId })
  case SOLUTION_REPORT_TYPE:
    return API.destroySolutionLevelScheduledReportExport({ reportId, scheduledReportExportId })
  case SURVEY_REPORT_TYPE:
    return API.destroySurveyScheduledReportExport({ reportId, scheduledReportExportId })
  case TASK_REPORT_TYPE:
    return API.destroyScheduledTaskReportExport({ reportId, scheduledReportExportId })
  default:
    return null
  }
}

export const deleteReport = createAsyncThunk(
  `${SLICE_NAME}/deleteReport`,
  async ({ reportType, reportId }) => {
    const response = await deleteReportRequest(reportType, reportId)

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to delete report.")
    }

    return { reportType, reportId }
  },
)

export const deleteScheduledReportExport = createAsyncThunk(
  `${SLICE_NAME}/deleteScheduledReportExport`,
  async ({ reportType, reportId, scheduledReportExportId }) => {
    const response = await deleteScheduledReportExportRequest(reportType, reportId, scheduledReportExportId)

    if (!response.ok) {
      throw new Error(`Error deleting SFTP uploads setting: ${response.data.errors.join(", ")}`)
    }

    return { reportType, reportId }
  },
)

export const activateSchedule = createAsyncThunk(
  `${SLICE_NAME}/activateSchedule`,
  async ({ reportType, scheduleId }) => {
    const response = await API.activateScheduledReportExportSchedule({ scheduleId })

    if (!response.ok) {
      throw new Error("Something went wrong. Unable to activate SFTP uploads")
    }

    return { reportType, scheduleId, active: true }
  },
)

export const deactivateSchedule = createAsyncThunk(
  `${SLICE_NAME}/deactivateSchedule`,
  async ({ reportType, scheduleId }) => {
    const response = await API.deactivateScheduledReportExportSchedule({ scheduleId })

    if (!response.ok) {
      throw new Error(`Unable to deactivate SFTP uploads: ${response.data.errors.join(", ")}.`)
    }

    return { reportType, scheduleId, active: false }
  },
)

const applyScheduleActiveStatus = (state, { reportType, scheduleId, active }) => {
  const report = state.reports[reportType].find(
    ({ scheduledReportExport }) => scheduledReportExport?.scheduledReportExportSchedule?.id === scheduleId,
  )

  if (report) report.scheduledReportExport.scheduledReportExportSchedule.active = active
}

export const reportIndexSlice = createSlice({
  name: SLICE_NAME,
  initialState: INITIAL_STATE,
  reducers: {
    setReports: (state, action) => {
      state.reports = { ...NO_REPORTS, ...action.payload }
    },
    setFilterSelections: (state, action) => {
      const {
        deselectedCategorySlugs = [],
        deselectedOwnershipOptions = [],
        deselectedSftpOptions = [],
      } = action.payload ?? {}

      state.deselectedCategorySlugs = deselectedCategorySlugs
      state.deselectedOwnershipOptions = deselectedOwnershipOptions
      state.deselectedSftpOptions = deselectedSftpOptions
    },
    selectCategory: (state, action) => {
      const { slug } = action.payload

      state.deselectedCategorySlugs = without(state.deselectedCategorySlugs, slug)
    },
    deselectCategory: (state, action) => {
      const { slug } = action.payload

      state.deselectedCategorySlugs = union(state.deselectedCategorySlugs, [slug])
    },
    selectAllCategories: (state) => {
      state.deselectedCategorySlugs = []
    },
    deselectAllCategories: (state, action) => {
      const { slugs } = action.payload

      state.deselectedCategorySlugs = slugs
    },
    selectOwnershipOption: (state, action) => {
      const { option } = action.payload

      state.deselectedOwnershipOptions = without(state.deselectedOwnershipOptions, option)
    },
    deselectOwnershipOption: (state, action) => {
      const { option } = action.payload

      state.deselectedOwnershipOptions = union(state.deselectedOwnershipOptions, [option])
    },
    selectSftpOption: (state, action) => {
      const { option } = action.payload

      state.deselectedSftpOptions = without(state.deselectedSftpOptions, option)
    },
    deselectSftpOption: (state, action) => {
      const { option } = action.payload

      state.deselectedSftpOptions = union(state.deselectedSftpOptions, [option])
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(deleteReport.fulfilled, (state, action) => {
        const { reportType, reportId } = action.payload

        state.reports[reportType] = state.reports[reportType].filter((report) => report.id !== reportId)
        successToast("Report deleted successfully!")
      })
      .addCase(deleteReport.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(deleteScheduledReportExport.fulfilled, (state, action) => {
        const { reportType, reportId } = action.payload

        const report = state.reports[reportType].find(({ id }) => id === reportId)

        if (report) report.scheduledReportExport = null
        successToast("SFTP uploads setting has been deleted.")
      })
      .addCase(deleteScheduledReportExport.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(activateSchedule.fulfilled, (state, action) => {
        applyScheduleActiveStatus(state, action.payload)
      })
      .addCase(activateSchedule.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
      .addCase(deactivateSchedule.fulfilled, (state, action) => {
        applyScheduleActiveStatus(state, action.payload)
      })
      .addCase(deactivateSchedule.rejected, (_state, action) => {
        errorToast(action.error.message)
      })
  },
})

export const {
  deselectAllCategories,
  deselectCategory,
  deselectOwnershipOption,
  deselectSftpOption,
  selectAllCategories,
  selectCategory,
  selectOwnershipOption,
  selectSftpOption,
  setFilterSelections,
  setReports,
} = reportIndexSlice.actions

export const getReports = (reportType) => (state) => state[SLICE_NAME].reports[reportType]

export const getFilteredReports = (reportType) => (state) => {
  const filters = filtersFromState(state)

  return getReports(reportType)(state).filter((report) => reportIsVisible(report, filters))
}

export const noReports = (state) => (
  Object.values(state[SLICE_NAME].reports).every((reports) => reports.length === 0)
)

export const noFilteredReports = (state) => {
  const filters = filtersFromState(state)

  return Object.values(state[SLICE_NAME].reports)
    .every((reports) => reports.every((report) => !reportIsVisible(report, filters)))
}

export const getScheduleActiveStatus = (reportType, scheduleId) => (state) => {
  const report = getReports(reportType)(state).find(
    ({ scheduledReportExport }) => scheduledReportExport?.scheduledReportExportSchedule?.id === scheduleId,
  )

  return report?.scheduledReportExport?.scheduledReportExportSchedule?.active ?? null
}

export const isCategorySelected = (slug) => (state) => !state[SLICE_NAME].deselectedCategorySlugs.includes(slug)

export const isOwnershipOptionSelected = (option) => (state) => (
  !state[SLICE_NAME].deselectedOwnershipOptions.includes(option)
)

export const isSftpOptionSelected = (option) => (state) => !state[SLICE_NAME].deselectedSftpOptions.includes(option)

export const settingsToPersist = (state) => (
  pick(state[SLICE_NAME], [
    "deselectedCategorySlugs",
    "deselectedOwnershipOptions",
    "deselectedSftpOptions",
  ])
)

export default reportIndexSlice.reducer
